import { useState, useMemo, useEffect } from 'react';
import { isTerminalMatterStatus, isArchivedMatterStatus } from '@/lib/matterStatus';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  MagnifyingGlass as Search,
  DownloadSimple as Download,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MatterStatusBadge } from '@/components/shared/MatterStatusBadge';
import { RowActions } from '@/components/shared/RowActions';
import { useMattersStore, type Matter, type MatterAccess } from '@/lib/mattersStore';
import { searchMatters, fetchMyBarristers, archiveMatter, restoreMatter } from '@/lib/mattersApi';
import { useDebounce } from '@/lib/useDebounce';
import { useAuthStore } from '@/features/auth/store';
import { onAllocationUpdate, onMattersUpdate } from '@/lib/websocket';
import { exportToCsv } from '@/lib/exportUtils';
import { EditMatterModal } from '@/components/shared/EditMatterModal';
import { canSeeMatter, type PrincipalLite } from '@/lib/permissions';
import { MatterHealthBadge, calculateMatterHealth } from '@/components/shared/MatterHealthBadge';
import { useMatterQuickAccessStore } from '@/lib/matterQuickAccessStore';
import { PinnedMattersBar } from '@/components/shared/PinnedMattersBar';
import { PushPin } from '@phosphor-icons/react';

const ACCESS_BADGE: Record<MatterAccess, { variant: 'default' | 'warning' | 'danger'; label: string }> = {
  normal:     { variant: 'default', label: 'Normal' },
  restricted: { variant: 'warning', label: 'Restricted' },
  private:    { variant: 'danger',  label: 'Private' },
};

function fmtFee(n: number | undefined): string {
  if (!n || n === 0) return '';
  return n >= 1000 ? `£${(n / 1000).toFixed(0)}k` : `£${n.toLocaleString('en-GB')}`;
}

const STATUS_FILTERS = [
  { label: 'All',      match: '' },
  { label: 'Active',   match: 'active' },
  { label: 'Listed',   match: 'listed' },
  { label: 'On hold',  match: 'on_hold' },
  { label: 'Closed',   match: 'closed' },
  { label: 'Archived', match: 'archived' },
];

const PAGE_SIZE = 5;

export default function CasesListPage({
  initialStatus,
  title,
  lockStatus,
}: { initialStatus?: string; title?: string; lockStatus?: boolean } = {}) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';
  const clerkFullName = user ? `${user.firstName} ${user.lastName}` : '';
  const storeMtrs = useMattersStore((s) => s.matters);
  const mattersTruncated = useMattersStore((s) => s.truncated);
  const principal: PrincipalLite | null = user
    ? { name: clerkFullName, role: isClerk ? 'clerk' : 'admin', memberType: user.memberType, userId: user.id }
    : null;

  // All clerks: fetch only the barristers they are linked to via BarristerClerk.
  // Seniority does NOT grant access to all matters — confidentiality requires
  // each clerk to see only matters they are directly connected to.
  const { data: linkedBarristers = [] } = useQuery({
    queryKey: ['my-barristers'],
    queryFn: fetchMyBarristers,
    enabled: isClerk,
    staleTime: 60_000,
  });
  // Convert to the ManagedBarrister shape canSeeMatter expects
  const managedBarristers = linkedBarristers.map((b) => ({
    name: `${b.firstName} ${b.lastName}${b.memberType === 'silk' ? ' KC' : ''}`,
    userId: b.userId,
    scopes: ['Matters'] as import('@/lib/useClerkWorkspace').Scope[],
  }));
  const fetchMatters = useMattersStore((s) => s.fetchMatters);

  // Fetch on mount, then refresh every 30s and whenever any allocation changes
  // (e.g. barrister accepts a brief in another browser window).
  useEffect(() => {
    fetchMatters();
    const poll = setInterval(fetchMatters, 300_000);
    const unsubAlloc = onAllocationUpdate(() => fetchMatters());
    const unsubMtrs  = onMattersUpdate(() => fetchMatters());
    return () => { clearInterval(poll); unsubAlloc(); unsubMtrs(); };
  }, [fetchMatters]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [statusFilter, setStatusFilter] = useState(initialStatus ?? '');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editMatter, setEditMatter] = useState<Matter | null>(null);
  const updateMatter = useMattersStore((s) => s.updateMatter);
  const refreshMatters = useMattersStore((s) => s.fetchMatters);
  // Pinned matters get a blue pin indicator on their row (pin via the matter's detail page).
  const pinnedMatters = useMatterQuickAccessStore((s) => s.pinnedMatters);
  const unpinMatterRow = useMatterQuickAccessStore((s) => s.unpinMatter);

  // When there's a search query, hit the FTS endpoint (GIN index).
  // When empty, fall back to the pre-loaded store data so the page
  // stays instant on first load.
  const searchQ = useQuery({
    queryKey: ['matters-search', debouncedSearch],
    queryFn: () => searchMatters(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    // Choose source: FTS results (when searching) vs full store (when idle).
    let data: Matter[] = debouncedSearch.length >= 2
      ? (searchQ.data ?? [])
      : storeMtrs;

    // Clerk access filtering: use proper permissions check
    if (isClerk && principal) {
      data = data.filter((m) => canSeeMatter(principal, m, managedBarristers));
    }
    if (statusFilter) {
      data = data.filter((c) =>
        statusFilter === 'closed'
          ? isTerminalMatterStatus(c.status) && !isArchivedMatterStatus(c.status)
          : c.status === statusFilter,
      );
    }
    // Pinned matters float to the top (stable — keeps existing order otherwise).
    const pinnedIds = new Set(pinnedMatters.map((p) => p.id));
    return [...data].sort((a, b) => (pinnedIds.has(b.id) ? 1 : 0) - (pinnedIds.has(a.id) ? 1 : 0));
  }, [storeMtrs, debouncedSearch, searchQ.data, statusFilter, isClerk, principal, managedBarristers, pinnedMatters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const allChecked = paged.length > 0 && paged.every((c) => selected.has(c.id));
  const toggleAll = () => {
    if (allChecked) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paged.map((c) => c.id)));
    }
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.info('No matters to export');
      return;
    }
    exportToCsv(
      filtered.map((m) => ({
        'Matter #': m.number,
        'Matter Name': m.name,
        'Status': m.status,
        'Lead Barrister': m.barrister,
        'Court': m.court,
        'Practice Area': m.area,
        'Next Hearing': m.next,
        'Fee Estimate': m.feeEstimate ? `£${m.feeEstimate.toLocaleString('en-GB')}` : '',
        'Created': m.createdAt ? new Date(m.createdAt).toLocaleDateString('en-GB') : '',
      })),
      `matters-export-${new Date().toISOString().slice(0, 10)}`,
    );
    toast.success(`Exported ${filtered.length} matter${filtered.length !== 1 ? 's' : ''} to CSV`);
  };

  const handleArchive = async (c: Matter) => {
    try {
      await archiveMatter(c.id);
      await refreshMatters();
      toast.success('Matter archived');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Could not archive matter';
      // If the guard blocked us because of future hearings, give the user a
      // one-click route to the matter's hearings tab so they can vacate them.
      const blockedByHearings = /hearing/i.test(msg);
      toast.error(msg, blockedByHearings ? {
        duration: 10_000,
        action: {
          label: 'Vacate hearings',
          onClick: () => navigate(`${basePath}/matters/${c.id}?tab=hearings`),
        },
      } : undefined);
    }
  };

  const handleRestore = async (c: Matter) => {
    try {
      await restoreMatter(c.id);
      await refreshMatters();
      toast.success('Matter restored to active');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Could not restore matter');
    }
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title={title ?? 'Matters'}
        subtitle={`${filtered.length} matters · ${storeMtrs.filter((c) => c.status === 'active').length} active · ${storeMtrs.filter((c) => c.status === 'listed').length} listed this month`}
        actions={
          <>
            <Button variant="outline" size="md" leftIcon={<Download className="h-4 w-4" />} onClick={handleExport}>Export</Button>
            <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate(`${basePath}/matters/new`)}>New matter</Button>
          </>
        }
      />

      <PinnedMattersBar basePath={basePath} navigate={navigate} />

      {/* Truncation warning — shown when the server returned the maximum fetch limit */}
      {mattersTruncated && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span className="font-semibold">Showing first 200 matters.</span>
          <span className="text-amber-700 dark:text-amber-300">Use the search bar to find older cases — they exist but are not loaded.</span>
        </div>
      )}

      {/* Filter bar */}
      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search matters — name, number, description, party…"
              className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            {searchQ.isFetching && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            )}
          </div>
          {/* Status filter tabs — hidden when the page is locked to a single status
              (e.g. the clerk Archive route, which only ever shows archived matters). */}
          {!lockStatus && (
            // Scrollable at phone width, like the contacts, hearing and My Matters strips.
            // Six filters are wider than a 390px screen: "Archived" sat past the right edge
            // with no way to reach it, and "On hold" was squashed into two lines getting there.
            // max-w-full is what makes the scroll bite — without it the group sizes to its
            // content and simply hangs off the edge. shrink-0 + nowrap keep each filter on one
            // line at its own width.
            <div className="no-scrollbar flex max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden rounded-md border border-border bg-card p-0.5 text-xs">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s.label}
                  onClick={() => { setStatusFilter(s.match); setPage(1); }}
                  className={`shrink-0 whitespace-nowrap rounded-sm px-3 py-1.5 font-medium transition ${
                    statusFilter === s.match ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100"
                    checked={allChecked}
                    onChange={toggleAll}
                  />
                </th>
                <th className="w-8"></th>
                <th>Matter #</th>
                <th>Matter name</th>
                <th>Court</th>
                <th>Lead barrister</th>
                <th>Next hearing</th>
                <th>Fee estimate</th>
                <th>Status</th>
                <th className="w-10 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((c) => {
                const health = calculateMatterHealth(c.updatedAt, c.nextHearingIso);
                const pinned = pinnedMatters.some((p) => p.id === c.id);
                return (
                <tr
                  key={c.id}
                  className={`group border-l-2 ${
                    c.id.startsWith('temp-') ? 'cursor-wait opacity-60' : 'cursor-pointer'
                  } ${
                    health.level === 'critical' ? 'border-l-red-500 bg-red-50/30 dark:bg-red-500/10'    :
                    health.level === 'at_risk'  ? 'border-l-amber-400 bg-amber-50/20 dark:bg-amber-500/10' :
                    'border-l-transparent'
                  }`}
                  onClick={() => { if (!c.id.startsWith('temp-')) navigate(`${basePath}/matters/${c.id}`); }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100"
                      checked={selected.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                    />
                  </td>
                  <td className="text-center">
                    <MatterHealthBadge
                      lastActivityDate={c.updatedAt}
                      nextHearingDate={c.nextHearingIso}
                      compact
                    />
                  </td>
                  <td className="text-xs text-brand-700 dark:text-brand-300">{c.number}</td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      {pinned && (
                        <button
                          type="button"
                          title="Pinned — click to unpin"
                          aria-label="Unpin matter"
                          onClick={(e) => { e.stopPropagation(); unpinMatterRow(c.id); toast.success('Matter unpinned'); }}
                          className="shrink-0 text-brand-600 dark:text-brand-400 transition hover:text-brand-700 dark:hover:text-brand-300"
                        >
                          <PushPin className="h-4 w-4" weight="fill" />
                        </button>
                      )}
                      <span className="font-medium text-foreground">{c.name}</span>
                      {c.access && c.access !== 'normal' && (
                        <Badge variant={ACCESS_BADGE[c.access].variant}>
                          {ACCESS_BADGE[c.access].label}
                        </Badge>
                      )}
                    </div>
                    {c.area && (
                      <div className="mt-0.5 text-[10px] text-muted-foreground">{c.area}</div>
                    )}
                  </td>
                  <td className="text-muted-foreground">{c.court}</td>
                  <td>
                    {c.barrister ? (
                      <span className="text-foreground">{c.barrister}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                        ● Unassigned
                      </span>
                    )}
                  </td>
                  <td>
                    {c.next && c.next !== '—' ? (
                      <div>
                        <span className={`text-xs ${health.level === 'critical' ? 'font-bold text-red-700 dark:text-red-300' : health.level === 'at_risk' ? 'font-semibold text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
                          {health.level === 'critical' && <span className="mr-1">●</span>}
                          {c.next}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="text-xs text-muted-foreground">
                    {fmtFee(c.feeEstimate) || <span className="text-muted-foreground/40">—</span>}
                  </td>
                  <td><MatterStatusBadge status={c.status} /></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {isTerminalMatterStatus(c.status) ? (
                      <RowActions
                        onView={() => navigate(`${basePath}/matters/${c.id}`)}
                        onEdit={() => setEditMatter(c)}
                        onDelete={() => handleRestore(c)}
                        deleteLabel="Restore"
                        deleteConfirm={{
                          title: 'Restore matter?',
                          message: `Restore “${c.name}” (${c.number}) to active?`,
                          confirmLabel: 'Restore',
                        }}
                      />
                    ) : (
                      <RowActions
                        onView={() => navigate(`${basePath}/matters/${c.id}`)}
                        onEdit={() => setEditMatter(c)}
                        onDelete={() => handleArchive(c)}
                        deleteLabel="Archive"
                        deleteConfirm={{
                          title: 'Archive matter?',
                          message: `Archive “${c.name}” (${c.number})? It will be removed from active lists but kept on record and can be restored later.`,
                          confirmLabel: 'Archive',
                        }}
                      />
                    )}
                  </td>
                </tr>
              );})}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-sm text-muted-foreground">
                    No matters match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
          <div>
            Showing <span className="text-foreground">{paged.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)}</span> of <span className="text-foreground">{filtered.length}</span> matters
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}>Previous</Button>
            <span className="px-2 text-xs text-foreground">{safePage} / {totalPages}</span>
            <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>Next</Button>
          </div>
        </div>
      </Card>

      <EditMatterModal
        open={!!editMatter}
        matter={editMatter}
        onClose={() => setEditMatter(null)}
        // Ending a matter belongs to the close ceremony, which lives on the matter itself.
        onCloseMatter={(id) => { setEditMatter(null); navigate(`${basePath}/matters/${id}?close=1`); }}
        onSave={async (id, patch) => {
          setEditMatter(null);
          if (await updateMatter(id, patch)) toast.success('Matter updated');
        }}
      />
    </div>
  );
}
